import { useState, useEffect } from 'react';


const GLOBAL_DEFAULTS = {
  ff: 100, stdMargin: 15, spp: 35,
  coef1: 1.45, irp: 0.76, drr: 5, turnover: 120, tax: 6, commission: 20, coefSklad: 1,
  logBase: 0, logExtra: 0, storageBase: 0, storageExtra: 0,
};

const SCROLL_GROUPS = [
  { label: 'Себес. товара',           color: '#1a1208', cols: [
    { label: 'Себес.',                    w: 80  },
    { label: 'ФФ',                        w: 60  },
    { label: 'Итог',                      w: 80  },
  ]},
  { label: 'Прибыль без РК',          color: '#0a1a14', cols: [
    { label: 'Чистая прибыль',           w: 100 },
    { label: 'Рента',                     w: 70  },
    { label: 'Маржа',                     w: 70  },
  ]},
  { label: 'Прибыль с РК',            color: '#091610', cols: [
    { label: 'Чистая прибыль',           w: 100 },
    { label: 'Рента',                     w: 70  },
    { label: 'Маржа',                     w: 70  },
  ]},
  { label: 'Ценообразование',         color: '#0d1526', cols: [
    { label: 'Цена до СПП',              w: 100 },
    { label: 'Цена с СПП',               w: 100 },
  ]},
  { label: 'Стоимость возврата',      color: '#0b1220', cols: [
    { label: 'Стоимость возврата',       w: 110 },
  ]},
  { label: 'Логистика WB + % выкупа', color: '#130f1a', cols: [
    { label: 'Процент выкупа',           w: 90  },
    { label: 'Логистика база',           w: 90  },
    { label: 'Объём (л)',                w: 80  },
    { label: 'ИРП',                      w: 50  },
    { label: 'Итог',                     w: 70  },
  ]},
  { label: 'Доля логистики',          color: '#0f111a', cols: [
    { label: 'Доля логистики',           w: 100 },
  ]},
  { label: 'ДРР',                     color: '#0f111a', cols: [
    { label: 'Заказ',                    w: 70  },
    { label: 'Выкуп',                    w: 70  },
  ]},
  { label: 'Хранение (в месяц)',      color: '#0b1220', cols: [
    { label: 'Сумма на 1 ед. (в день)', w: 120 },
    { label: 'Итог',                     w: 70  },
  ]},
  { label: 'Комиссия WB',             color: '#1a0d0d', cols: [
    { label: 'Комиссия на 1 ед.',       w: 110 },
    { label: 'Итог',                     w: 70  },
  ]},
  { label: 'Сумма налога',            color: '#160b0b', cols: [
    { label: '% Налога',                 w: 70  },
    { label: 'Итог',                     w: 70  },
  ]},
];

const SCROLL_COLS = SCROLL_GROUPS.flatMap(g => g.cols.map(c => ({ ...c, groupColor: g.color, groupAccent: g.accent || null })));
const ROW_H   = 46;
const HEAD1_H = 30;
const HEAD2_H = 28;
const FIXED_W = 180;
const TOTAL_W = FIXED_W + SCROLL_COLS.reduce((a,b) => a+b.w, 0);
const FIXED_BG = '#0d1420';

function GlobalParam({ label, value, onChange, suffix='₽' }) {
  return (
    <div style={{display:'flex',alignItems:'center',gap:6,background:'#0f111a',border:'1px solid rgba(255,255,255,0.08)',borderRadius:8,padding:'5px 10px'}}>
      {label && <span style={{fontSize:11,color:'rgba(200,215,235,0.55)',whiteSpace:'nowrap'}}>{label}:</span>}
      <input type="number" value={value??''} onChange={e=>onChange(e.target.value===''?null:Number(e.target.value))}
        style={{width:56,background:'transparent',border:'none',color:'#fff',fontSize:12,fontWeight:600,padding:'2px 0',outline:'none',textAlign:'right'}}/>
      {suffix && <span style={{fontSize:11,color:'rgba(200,215,235,0.4)'}}>{suffix}</span>}
    </div>
  );
}

export default function StrategyUnitTable({ plan, onChange, seasonDays, seasonStart, onStatsChange, unitStats }) {
  // params — глобальные параметры (хранятся в plan.params)
  const params = { ...GLOBAL_DEFAULTS, ...(plan.params || {}) };
  // rowData — ручные значения ячеек (хранятся в plan.cells)
  const rowData = plan.cells || {};
  const [collapsedBatch, setCollapsedBatch] = useState(()=>(plan?.cells||{})['__batchCollapsed']===true);
  const [collapsedCompetitor, setCollapsedCompetitor] = useState(()=>(plan?.cells||{})['__competitorCollapsed']===true);
  const [collapsedAdCalc, setCollapsedAdCalc] = useState(()=>(plan?.cells||{})['__adCalcCollapsed']===true);
  const [collapsedSliv, setCollapsedSliv] = useState(()=>(plan?.cells||{})['__slivCollapsed']===true);
  const stdMargin = params.stdMargin || 15;

  const setParam = (key) => (val) => {
    onChange({ ...plan, params: { ...params, [key]: val } });
  };

  const setCellVal = (colLabel, groupColor, val) => {
    const cellKey = colLabel + '|' + groupColor;
    const globalDefault = getGlobalDefault(colLabel, groupColor);
    const storeVal = (globalDefault !== null && val === String(globalDefault)) ? null : val;
    const newCells = { ...rowData, [cellKey]: storeVal };
    if (colLabel === 'Цена до СПП') delete newCells['Цена с СПП|' + groupColor];
    onChange({ ...plan, cells: newCells });
  };

  const getGlobalDefault = (colLabel, groupColor) => {
    if (colLabel === 'ФФ' && groupColor === '#1a1208') return String(params.ff);
    if (colLabel === '% Налога') return String(params.tax);
    if (colLabel === 'Комиссия на 1 ед.') return String(params.commission);
    return null;
  };

  const calcLogBase = (vol) => {
    if (!vol || vol <= 0) return null;
    const v = Number(vol);
    if (v > 1) return Math.round((Number(params.logBase) + (v-1) * Number(params.logExtra)) * 10) / 10;
    if (v <= 0.200) return 23;
    if (v <= 0.400) return 26;
    if (v <= 0.600) return 29;
    if (v <= 0.800) return 30;
    return 32;
  };

  // Аналог getEV — но данные берём из rowData напрямую
  const getEV = () => {
    const price    = Number(rowData['Цена до СПП|#0d1526'] ?? 0);
    const sppPct   = Number(params.spp ?? 35);
    const manualSpp = rowData['Цена с СПП|#0d1526'];
    const priceSpp = manualSpp != null ? Number(manualSpp) : Math.round(price*(1-sppPct/100)*100)/100;
    const cost     = Number(rowData['Себес.|#1a1208'] ?? 0);
    const ff       = Number(rowData['ФФ|#1a1208'] ?? params.ff);
    const itogSeb  = cost + ff;
    const vol      = rowData['Объём (л)|#130f1a'];
    const volN     = Number(vol) || 0;
    const logBase  = Number(rowData['Логистика база|#130f1a'] ?? calcLogBase(vol) ?? 0);
    const vozvrat  = Number(rowData['Стоимость возврата|#0b1220'] ?? logBase);
    const buyout   = Number(rowData['Процент выкупа|#130f1a'] ?? 30);
    const il       = Number(params.coef1 || 1);
    const koef     = Number(params.coefSklad || 1);
    const irp      = price > 0 ? Math.round(params.irp*price/100*100)/100 : 0;
    const deliv    = logBase*koef*il+irp;
    const itogLog  = buyout > 0 ? Math.round(((100-buyout)*(vozvrat+deliv)/buyout+deliv)*100)/100 : 0;
    const baseH    = Number(params.storageBase || 0);
    const extraH   = Number(params.storageExtra || 0);
    const sumDayAuto = volN > 0 ? (volN>1 ? Math.round((baseH+(volN-1)*extraH)*1000)/1000 : Math.round(baseH*1000)/1000) : 0;
    const sumDay   = rowData['Сумма на 1 ед. (в день)|#0b1220'] != null ? Number(rowData['Сумма на 1 ед. (в день)|#0b1220']) : sumDayAuto;
    const itogHran = Math.round(sumDay*Number(params.turnover||0)*100)/100;
    const commPct  = Number(rowData['Комиссия на 1 ед.|#1a0d0d'] ?? params.commission ?? 0);
    const itogComm = price > 0 ? Math.round(price*commPct/100*100)/100 : 0;
    const taxPct   = Number(rowData['% Налога|#160b0b'] ?? params.tax ?? 0);
    const itogTax  = priceSpp > 0 ? Math.round(priceSpp*taxPct/100*100)/100 : 0;
    return { price, priceSpp, cost, ff, itogSeb, vol, volN, logBase, vozvrat, buyout, il, koef, irp, deliv, itogLog, sumDay, itogHran, commPct, itogComm, taxPct, itogTax };
  };

  const ev = getEV();

  useEffect(() => {
    if (!onStatsChange) return;
    const drrOnOrder = Math.round(ev.price * (params.drr!=null?Number(params.drr):5) / 100 * 100)/100;
    const drrVal = ev.buyout > 0 ? Math.round(drrOnOrder / (ev.buyout/100) * 100)/100 : 0;
    const pRK  = ev.price > 0 ? Math.round((ev.price - ev.itogSeb - ev.itogLog - ev.itogHran - ev.itogComm - ev.itogTax - drrVal)*100)/100 : null;
    const pNoRK= ev.price > 0 ? Math.round((ev.price - ev.itogSeb - ev.itogLog - ev.itogHran - ev.itogComm - ev.itogTax)*100)/100 : null;
    onStatsChange({ profitRK: pRK, profitNoRK: pNoRK, buyoutAvg: ev.buyout });
  }, [ev.price, ev.itogSeb, ev.itogLog, ev.itogHran, ev.itogComm, ev.itogTax, ev.buyout, params.drr]);

  const calcNetProfit = (withRK) => {
    const drrOnOrder = Math.round(ev.price * (params.drr!=null?Number(params.drr):5) / 100 * 100)/100;
    const drrVal = withRK && ev.buyout > 0 ? Math.round(drrOnOrder / (ev.buyout/100) * 100)/100 : 0;
    return ev.price > 0 ? Math.round((ev.price - ev.itogSeb - ev.itogLog - ev.itogHran - ev.itogComm - ev.itogTax - drrVal)*100)/100 : null;
  };

  const getCellData = (col) => {
    let cellVal = '', autoVal = null, isReadOnly = false;

    if (col.label === 'Себес.' && col.groupColor === '#1a1208') {
      const manual = rowData['Себес.|' + col.groupColor];
      autoVal = ''; cellVal = manual != null ? manual : autoVal;
    } else if (col.label === 'ФФ' && col.groupColor === '#1a1208') {
      autoVal = String(params.ff);
      const manual = rowData['ФФ|' + col.groupColor];
      cellVal = manual != null ? manual : autoVal;
    } else if (col.label === 'Итог' && col.groupColor === '#1a1208') {
      cellVal = ev.itogSeb > 0 ? String(ev.itogSeb) : ''; isReadOnly = true;
    } else if (col.label === 'Чистая прибыль' && col.groupColor === '#0a1a14') {
      const cp = calcNetProfit(false);
      autoVal = cp !== null ? String(cp) : null;
      const manual = rowData['Чистая прибыль|' + col.groupColor];
      cellVal = manual != null ? manual : (autoVal !== null ? autoVal : '');
    } else if (col.label === 'Рента' && col.groupColor === '#0a1a14') {
      const cp = calcNetProfit(false);
      autoVal = ev.itogSeb > 0 && cp !== null ? String(Math.round(cp/ev.itogSeb*100*100)/100)+'%' : null;
      const manual = rowData['Рента|' + col.groupColor];
      cellVal = manual != null ? manual : (autoVal !== null ? autoVal : '');
    } else if (col.label === 'Маржа' && col.groupColor === '#0a1a14') {
      const cp = calcNetProfit(false);
      autoVal = ev.price > 0 && cp !== null ? String(Math.round(cp/ev.price*100*100)/100)+'%' : null;
      const manual = rowData['Маржа|' + col.groupColor];
      cellVal = manual != null ? manual : (autoVal !== null ? autoVal : '');
    } else if (col.label === 'Чистая прибыль' && col.groupColor === '#091610') {
      const cp = calcNetProfit(true);
      autoVal = cp !== null ? String(cp) : null;
      const manual = rowData['Чистая прибыль|' + col.groupColor];
      cellVal = manual != null ? manual : (autoVal !== null ? autoVal : '');
    } else if (col.label === 'Рента' && col.groupColor === '#091610') {
      const cp = calcNetProfit(true);
      autoVal = ev.itogSeb > 0 && cp !== null ? String(Math.round(cp/ev.itogSeb*100*100)/100)+'%' : null;
      const manual = rowData['Рента|' + col.groupColor];
      cellVal = manual != null ? manual : (autoVal !== null ? autoVal : '');
    } else if (col.label === 'Маржа' && col.groupColor === '#091610') {
      const cp = calcNetProfit(true);
      autoVal = ev.price > 0 && cp !== null ? String(Math.round(cp/ev.price*100*100)/100)+'%' : null;
      const manual = rowData['Маржа|' + col.groupColor];
      cellVal = manual != null ? manual : (autoVal !== null ? autoVal : '');
    } else if (col.label === 'Цена до СПП') {
      const manual = rowData['Цена до СПП|' + col.groupColor];
      autoVal = ''; cellVal = manual != null ? manual : autoVal;
    } else if (col.label === 'Цена с СПП') {
      const sppVal = ev.priceSpp;
      autoVal = sppVal > 0 ? String(sppVal) : '';
      const manual = rowData['Цена с СПП|' + col.groupColor];
      cellVal = manual != null ? manual : autoVal;
    } else if (col.label === 'Стоимость возврата') {
      const manual = rowData['Стоимость возврата|' + col.groupColor];
      autoVal = String(ev.logBase);
      cellVal = manual != null ? manual : autoVal;
    } else if (col.label === 'Процент выкупа') {
      autoVal = '30';
      const manual = rowData['Процент выкупа|' + col.groupColor];
      cellVal = manual != null ? manual : autoVal;
    } else if (col.label === 'Логистика база') {
      const auto = calcLogBase(rowData['Объём (л)|#130f1a']);
      const manual = rowData['Логистика база|' + col.groupColor];
      autoVal = auto != null ? String(auto) : null;
      cellVal = manual != null ? manual : (autoVal !== null ? autoVal : '');
    } else if (col.label === 'Объём (л)') {
      const manual = rowData['Объём (л)|' + col.groupColor];
      cellVal = manual != null ? manual : '';
    } else if (col.label === 'ИРП') {
      cellVal = ev.price > 0 ? String(Math.round(ev.irp*100)/100) : ''; isReadOnly = true;
    } else if (col.label === 'Итог' && col.groupColor === '#130f1a') {
      autoVal = String(ev.itogLog);
      const manual = rowData['Итог|' + col.groupColor];
      cellVal = manual != null ? manual : autoVal;
    } else if (col.label === 'Доля логистики') {
      const val = ev.price > 0 && ev.itogLog > 0 ? String(Math.round(ev.itogLog/ev.price*100*100)/100)+'%' : null;
      cellVal = val !== null ? val : ''; isReadOnly = true;
    } else if (col.label === 'Заказ' && col.groupColor === '#0f111a') {
      autoVal = ev.price > 0 ? String(Math.round(ev.price*(params.drr!=null?Number(params.drr):5)/100*100)/100) : null;
      const manual = rowData['Заказ|' + col.groupColor];
      cellVal = manual != null ? manual : (autoVal !== null ? autoVal : '');
    } else if (col.label === 'Выкуп') {
      const drrZ = (() => {
        const manual = rowData['Заказ|#0f111a'];
        return manual != null ? Number(manual) : (ev.price > 0 ? Math.round(ev.price*(params.drr!=null?Number(params.drr):5)/100*100)/100 : 0);
      })();
      autoVal = drrZ > 0 && ev.buyout > 0 ? String(Math.round((drrZ/ev.buyout)*100*100)/100) : null;
      const manual = rowData['Выкуп|' + col.groupColor];
      cellVal = manual != null ? manual : (autoVal !== null ? autoVal : '');
    } else if (col.label === 'Сумма на 1 ед. (в день)') {
      const volN = Number(rowData['Объём (л)|#130f1a']);
      const base = Number(params.storageBase||0);
      const extra = Number(params.storageExtra||0);
      const auto = volN > 0 ? (volN>1 ? Math.round((base+(volN-1)*extra)*1000)/1000 : Math.round(base*1000)/1000) : null;
      autoVal = auto !== null ? String(auto) : null;
      const manual = rowData['Сумма на 1 ед. (в день)|' + col.groupColor];
      cellVal = manual != null ? manual : (autoVal !== null ? autoVal : '');
    } else if (col.label === 'Итог' && col.groupColor === '#0b1220') {
      autoVal = String(ev.itogHran);
      const manual = rowData['Итог|' + col.groupColor];
      cellVal = manual != null ? manual : autoVal;
    } else if (col.label === 'Комиссия на 1 ед.') {
      autoVal = String(params.commission);
      const manual = rowData['Комиссия на 1 ед.|' + col.groupColor];
      cellVal = manual != null ? manual : autoVal;
    } else if (col.label === 'Итог' && col.groupColor === '#1a0d0d') {
      autoVal = ev.itogComm > 0 ? String(ev.itogComm) : null;
      const manual = rowData['Итог|' + col.groupColor];
      cellVal = manual != null ? manual : (autoVal !== null ? autoVal : '');
    } else if (col.label === '% Налога') {
      autoVal = String(params.tax);
      const manual = rowData['% Налога|' + col.groupColor];
      cellVal = manual != null ? manual : autoVal;
    } else if (col.label === 'Итог' && col.groupColor === '#160b0b') {
      autoVal = ev.itogTax > 0 ? String(ev.itogTax) : null;
      const manual = rowData['Итог|' + col.groupColor];
      cellVal = manual != null ? manual : (autoVal !== null ? autoVal : '');
    } else {
      const globalDefault = getGlobalDefault(col.label, col.groupColor);
      const manual = rowData[col.label+'|'+col.groupColor];
      if (manual != null) cellVal = manual;
      else if (globalDefault !== null) { cellVal = globalDefault; autoVal = globalDefault; }
    }

    const noRound = ['Объём (л)', 'Сумма на 1 ед. (в день)', 'ИРП', 'Маржа', 'Заказ', 'Выкуп'];
    if (!noRound.includes(col.label) && cellVal !== '' && !isNaN(parseFloat(cellVal))) {
      const numVal = parseFloat(cellVal);
      const hasPct = String(cellVal).includes('%');
      if (!hasPct) cellVal = String(Math.round(numVal));
    }

    return { cellVal, autoVal, isReadOnly };
  };

  return (
    <div>
      {/* Глобальные параметры */}
      <div style={{background:'rgba(8,14,25,0.7)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:12,padding:'12px 16px',marginBottom:8,display:'flex',flexWrap:'wrap',gap:8,alignItems:'center'}}>
        <span style={{fontSize:11,color:'rgba(200,215,235,0.4)',marginRight:4,letterSpacing:'0.5px',textTransform:'uppercase'}}>Параметры:</span>
        <GlobalParam label="ФФ"                      value={params.ff}          onChange={setParam('ff')}          suffix="₽"/>
        <GlobalParam label="Стандарт маржинальности" value={params.stdMargin}   onChange={setParam('stdMargin')}   suffix="%"/>
        <GlobalParam label="ИЛ"                      value={params.coef1}       onChange={setParam('coef1')}       suffix=""/>
        <GlobalParam label="ИРП"                     value={params.irp}         onChange={setParam('irp')}         suffix=""/>
        <GlobalParam label="ДРР"                     value={params.drr}         onChange={setParam('drr')}         suffix="%"/>
        <GlobalParam label="Оборачиваемость"         value={params.turnover}    onChange={setParam('turnover')}    suffix="дн"/>
        <GlobalParam label="Налог"                   value={params.tax}         onChange={setParam('tax')}         suffix="%"/>
        <GlobalParam label="Комиссия WB"             value={params.commission}  onChange={setParam('commission')}  suffix="%"/>
        <GlobalParam label="Коэф склада"             value={params.coefSklad}   onChange={setParam('coefSklad')}   suffix=""/>
        <GlobalParam label="СПП"                     value={params.spp}         onChange={setParam('spp')}         suffix="%"/>
      </div>
      <div style={{background:'rgba(8,14,25,0.7)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:12,padding:'12px 16px',marginBottom:8,display:'flex',flexWrap:'wrap',gap:16,alignItems:'center'}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:11,color:'rgba(200,215,235,0.4)',letterSpacing:'0.5px',textTransform:'uppercase'}}>Логистика:</span>
          <GlobalParam label="Базовая стоимость литра" value={params.logBase}    onChange={setParam('logBase')}    suffix="₽"/>
          <GlobalParam label="Цена за доп литр"        value={params.logExtra}   onChange={setParam('logExtra')}   suffix="₽"/>
        </div>
        <div style={{width:1,height:28,background:'rgba(255,255,255,0.07)'}}/>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:11,color:'rgba(200,215,235,0.4)',letterSpacing:'0.5px',textTransform:'uppercase'}}>Хранение:</span>
          <GlobalParam label="Базовая стоимость литра" value={params.storageBase}  onChange={setParam('storageBase')}  suffix="₽"/>
          <GlobalParam label="Цена за доп литр"        value={params.storageExtra} onChange={setParam('storageExtra')} suffix="₽"/>
        </div>
      </div>

      {/* Таблица */}
      <div style={{background:'#0a0f1a',border:'1px solid rgba(255,255,255,0.12)',borderRadius:12,overflow:'auto'}}>
        <div style={{minWidth:TOTAL_W}}>
          {/* Шапка строка 1 */}
          <div style={{display:'flex',position:'sticky',top:0,zIndex:4,background:'#0a0f1a'}}>
            <div style={{width:FIXED_W,minWidth:FIXED_W,flexShrink:0,height:HEAD1_H,position:'sticky',left:0,zIndex:5,background:FIXED_BG,
              display:'flex',alignItems:'center',justifyContent:'center',
              borderBottom:'1px solid rgba(255,255,255,0.06)',borderRight:'2px solid rgba(255,255,255,0.18)',
              fontSize:11,fontWeight:600,color:'rgba(200,215,235,0.75)',boxSizing:'border-box'}}>
              Наименование
            </div>
            {SCROLL_GROUPS.map((g,gi)=>{
              const gw = g.cols.reduce((a,b)=>a+b.w,0);
              return (
                <div key={gi} style={{width:gw,minWidth:gw,flexShrink:0,height:HEAD1_H,
                  display:'flex',alignItems:'center',justifyContent:'center',
                  background:g.color,borderRight:'1px solid rgba(255,255,255,0.07)',
                  borderBottom:'1px solid rgba(255,255,255,0.06)',
                  fontSize:11,fontWeight:600,color:g.accent||'rgba(200,215,235,0.7)',
                  whiteSpace:'nowrap',boxSizing:'border-box'}}>
                  {g.label}
                </div>
              );
            })}
          </div>
          {/* Шапка строка 2 */}
          <div style={{display:'flex',position:'sticky',top:HEAD1_H,zIndex:4,background:'#0a0f1a'}}>
            <div style={{width:FIXED_W,minWidth:FIXED_W,flexShrink:0,height:HEAD2_H,position:'sticky',left:0,zIndex:5,background:FIXED_BG,
              display:'flex',alignItems:'center',padding:'0 12px',
              borderBottom:'1px solid rgba(255,255,255,0.08)',borderRight:'2px solid rgba(255,255,255,0.18)',
              fontSize:10,fontWeight:500,color:'rgba(200,215,235,0.5)',boxSizing:'border-box'}}>
            </div>
            {SCROLL_COLS.map((col,ci)=>(
              <div key={ci} style={{width:col.w,minWidth:col.w,flexShrink:0,height:HEAD2_H,
                display:'flex',alignItems:'center',justifyContent:'center',
                background:col.groupColor,borderRight:'1px solid rgba(255,255,255,0.04)',
                borderBottom:'1px solid rgba(255,255,255,0.08)',
                fontSize:10,fontWeight:500,color:col.groupAccent||'rgba(200,215,235,0.5)',
                whiteSpace:'nowrap',padding:'0 6px',boxSizing:'border-box'}}>
                {col.label}
              </div>
            ))}
          </div>
          {/* Строка данных */}
          <div style={{display:'flex',borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
            <div style={{width:FIXED_W,minWidth:FIXED_W,flexShrink:0,height:ROW_H,position:'sticky',left:0,zIndex:2,background:FIXED_BG,
              borderRight:'2px solid rgba(255,255,255,0.18)',boxSizing:'border-box',
              display:'flex',alignItems:'center',padding:'0 12px'}}>
              <div>
                <div style={{fontSize:11,fontWeight:600,color:'#fff',marginBottom:2}}>Мой план</div>
                <div style={{fontSize:9,color:'rgba(200,215,235,0.3)'}}>Заполни ячейки вручную</div>
              </div>
            </div>
            {SCROLL_COLS.map((col,ci)=>{
              const {cellVal, autoVal, isReadOnly} = getCellData(col);
              const isEmpty = cellVal === '';
              const manualVal = rowData[col.label+'|'+col.groupColor];
              const globalDefault = getGlobalDefault(col.label, col.groupColor);
              const effectiveAuto = autoVal !== null ? autoVal : (globalDefault !== null ? globalDefault : '');
              const isManual = manualVal != null && manualVal !== '' && String(manualVal) !== String(effectiveAuto);
              const cpNoRK = calcNetProfit(false);
              const cpRK   = calcNetProfit(true);
              const isGoodMargin = col.label==='Маржа' && col.groupColor==='#091610' && cellVal && parseFloat(cellVal) > stdMargin;
              const isBadMargin  = col.label==='Маржа' && col.groupColor==='#091610' && cellVal && parseFloat(cellVal) < 0;
              return (
                <div key={ci} style={{
                  width:col.w,minWidth:col.w,flexShrink:0,height:ROW_H,
                  display:'flex',alignItems:'center',justifyContent:'center',
                  background: isGoodMargin?'rgba(0,255,80,0.35)':isBadMargin?'rgba(239,68,68,0.25)':isManual?'rgba(59,130,246,0.08)':col.groupColor,
                  borderRight:'1px solid rgba(255,255,255,0.03)',
                  borderBottom: isManual?'1px solid rgba(59,130,246,0.3)':'none',
                  boxSizing:'border-box',padding:'0 4px',
                }}>
                  <input value={cellVal} readOnly={isReadOnly} placeholder="—"
                    onChange={e=>{ if(!isReadOnly) setCellVal(col.label, col.groupColor, e.target.value.replace(',','.')); }}
                    style={{width:'100%',background:'transparent',border:'none',
                      color:isEmpty?'rgba(200,215,235,0.2)':'rgba(200,215,235,0.85)',
                      fontSize:10,textAlign:'center',outline:'none',
                      cursor:isReadOnly?'default':'text',padding:0}}
                    onFocus={e=>{ if(!isReadOnly) e.target.style.background='rgba(255,255,255,0.05)'; }}
                    onBlur={e=>e.target.style.background='transparent'}/>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Блок Партия */}
      <div style={{ marginTop:16, background:'#0a0f1a', border:'1px solid rgba(255,255,255,0.12)', borderRadius:12, padding:'16px 20px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: collapsedBatch ? 0 : 16, cursor:'pointer' }} onClick={()=>{const v=!collapsedBatch;setCollapsedBatch(v);if(onChange&&plan)onChange({...plan,cells:{...(plan.cells||{}),__batchCollapsed:v}});}}>
          <span style={{ fontSize:13, fontWeight:700, color:'rgba(200,215,235,0.7)' }}>Партия</span>
          <span style={{ fontSize:12, color:'rgba(200,215,235,0.4)' }}>{collapsedBatch ? '▶' : '▼'}</span>
        </div>
        {!collapsedBatch && <div style={{ display:'flex', alignItems:'center', gap:24, flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>

          <input type="number" value={rowData['__batchQty'] ?? ''} placeholder="шт"
            onChange={e=>onChange({ ...plan, cells: { ...rowData, __batchQty: e.target.value===''?null:Number(e.target.value) } })}
            style={{ width:110, background:'#0f111a', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, padding:'7px 12px', outline:'none', textAlign:'right' }}/>
          <span style={{ fontSize:12, color:'rgba(200,215,235,0.4)' }}>шт</span>
        </div>
        <div style={{ width:1, height:36, background:'rgba(255,255,255,0.1)' }}/>
        {(() => {
          const qty = Number(rowData['__batchQty']) || 0;
          const cpNoRK = calcNetProfit(false);
          const cpRK   = calcNetProfit(true);
          const totalNoRK = cpNoRK !== null ? Math.round(cpNoRK * qty) : null;
          const totalRK   = cpRK   !== null ? Math.round(cpRK   * qty) : null;
          const fmt = (v) => v === null ? '—' : v.toLocaleString('ru-RU') + ' ₽';
          const clr = (v) => v === null ? 'rgba(200,215,235,0.3)' : v < 0 ? '#ef4444' : '#10b981';
          return (
            <>
              <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                <span style={{ fontSize:10, color:'rgba(200,215,235,0.45)', textTransform:'uppercase', letterSpacing:'0.5px' }}>Прибыль с партии без РК</span>
                <span style={{ fontSize:18, fontWeight:700, color:clr(totalNoRK) }}>{fmt(totalNoRK)}</span>
              </div>
              <div style={{ width:1, height:36, background:'rgba(255,255,255,0.1)' }}/>
              <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                <span style={{ fontSize:10, color:'rgba(200,215,235,0.45)', textTransform:'uppercase', letterSpacing:'0.5px' }}>Прибыль с партии с РК</span>
                <span style={{ fontSize:18, fontWeight:700, color:clr(totalRK) }}>{fmt(totalRK)}</span>
              </div>
              <div style={{ width:1, height:36, background:'rgba(255,255,255,0.1)' }}/>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:11, fontWeight:700, color:'rgba(200,215,235,0.6)' }}>% на старт:</span>
                <input type="number" value={rowData['__startPct'] ?? ''} placeholder="%"
                  onChange={e=>onChange({ ...plan, cells: { ...rowData, __startPct: e.target.value===''?null:Number(e.target.value) } })}
                  style={{ width:70, background:'#0f111a', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, padding:'7px 10px', outline:'none', textAlign:'right' }}/>
              </div>
              {(() => {
                const cpRK2 = calcNetProfit(true);
                const qty2 = Number(rowData['__batchQty']) || 0;
                const totalRK2 = cpRK2 !== null ? Math.round(cpRK2 * qty2) : null;
                const startPct = Number(rowData['__startPct']) || 0;
                const days = Number(seasonDays) || 0;
                if (totalRK2 === null || totalRK2 <= 0 || startPct <= 0 || days <= 0) return null;
                const invest = Math.round(totalRK2 * startPct / 100);
                const profitPerDay = Math.round(totalRK2 / days);
                const paybackDays = profitPerDay > 0 ? Math.ceil(invest / profitPerDay) : null;
                const remaining = totalRK2 - invest;
                // В заказах/выкупах
                const buyout = Number(rowData['Процент выкупа|#130f1a'] ?? params.buyout ?? 30);
                const ordersPerDay = profitPerDay > 0 && cpRK2 > 0 ? Math.round(profitPerDay / cpRK2 / (buyout/100) * 10) / 10 : null;
                const buyoutsPerDay = ordersPerDay !== null ? Math.round(ordersPerDay * buyout / 100 * 10) / 10 : null;
                let plusDate = '—';
                if (seasonStart && paybackDays !== null) {
                  const dt = new Date(seasonStart);
                  dt.setDate(dt.getDate() + paybackDays);
                  plusDate = dt.toLocaleDateString('ru-RU');
                }
                const fmtR = (v) => v.toLocaleString('ru-RU') + ' ₽';
                const M = ({ label, value, accent }) => (
                  <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                    <span style={{ fontSize:10, color:'rgba(200,215,235,0.45)', textTransform:'uppercase', letterSpacing:'0.5px' }}>{label}</span>
                    <span style={{ fontSize:14, fontWeight:700, color: accent || 'rgba(200,215,235,0.9)' }}>{value}</span>
                  </div>
                );
                return (
                  <>
                    <div style={{ width:1, height:36, background:'rgba(255,255,255,0.1)' }}/>
                    <M label={`Инвестиция (${startPct}%)`} value={fmtR(invest)} accent="#f59e0b"/>
                    <div style={{ width:1, height:36, background:'rgba(255,255,255,0.1)' }}/>
                    <M label="Окупаемость" value={paybackDays !== null ? paybackDays + ' дн' : '—'}/>
                    <div style={{ width:1, height:36, background:'rgba(255,255,255,0.1)' }}/>
                    <M label="Дата выхода в плюс" value={plusDate} accent="#10b981"/>
                    <div style={{ width:1, height:36, background:'rgba(255,255,255,0.1)' }}/>
                    <M label="Прибыль после окупаемости" value={fmtR(remaining)} accent="#10b981"/>
                    {ordersPerDay !== null && <>
                      <div style={{ width:1, height:36, background:'rgba(255,255,255,0.1)' }}/>
                      <M label={`Нужно заказов/день (выкуп ${buyout}%)`} value={`${ordersPerDay} шт → ${buyoutsPerDay} выкупов`} accent="rgba(200,215,235,0.7)"/>
                    </>}
                  </>
                );
              })()}
            </>
          );
        })()}
        </div>}
      </div>



      {/* Анализ конкурентов */}

      {/* Прогноз окупаемости по данным конкурента */}
      {(() => {
        const cpRK3 = calcNetProfit(true);
        const qty3 = Number(rowData['__batchQty']) || 0;
        const totalRK3 = cpRK3 !== null ? Math.round(cpRK3 * qty3) : null;
        const startPct = Number(rowData['__startPct']) || 0;
        const invest = (totalRK3 !== null && startPct > 0) ? Math.round(totalRK3 * startPct / 100) : 0;
        const compOrders = Number(rowData['__compOrders']) || 0;
        const compBuyout = Number(rowData['__compBuyout']) || 0;
        const buyoutsWeek = Math.round(compOrders * compBuyout / 100);
        const profitWeek = (cpRK3 !== null) ? Math.round(buyoutsWeek * cpRK3) : 0;
        const weeks = profitWeek > 0 && invest > 0 ? Math.ceil(invest / profitWeek) : null;
        let plusDate = '—';
        if (seasonStart && weeks !== null) {
          const dt = new Date(seasonStart);
          dt.setDate(dt.getDate() + weeks * 7);
          plusDate = dt.toLocaleDateString('ru-RU');
        }
        const fmtR = (v) => v.toLocaleString('ru-RU') + ' ₽';
        const M = ({ label, value, accent }) => (
          <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
            <span style={{ fontSize:10, color:'rgba(200,215,235,0.45)', textTransform:'uppercase', letterSpacing:'0.5px' }}>{label}</span>
            <span style={{ fontSize:16, fontWeight:700, color: accent || 'rgba(200,215,235,0.9)' }}>{value}</span>
          </div>
        );
        return (
          <div style={{ marginTop:16, background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.35)', borderRadius:12, padding:'16px 20px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4, cursor:'pointer' }} onClick={()=>{const v=!collapsedCompetitor;setCollapsedCompetitor(v);if(onChange&&plan)onChange({...plan,cells:{...(plan.cells||{}),__competitorCollapsed:v}});}}>
            <span style={{ fontSize:13, fontWeight:700, color:'#60a5fa' }}>Прогноз окупаемости по данным конкурента</span>
            <span style={{ fontSize:12, color:'rgba(200,215,235,0.4)' }}>{collapsedCompetitor ? '▶' : '▼'}</span>
          </div>
          {!collapsedCompetitor && <><div style={{ fontSize:11, color:'rgba(200,215,235,0.45)', marginBottom:14 }}>Независимый расчёт: вводишь заказы и % выкупа конкурента за неделю — считает, за сколько недель окупится инвестиция на старт.</div>
            <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:11, fontWeight:700, color:'rgba(200,215,235,0.6)' }}>Заказы/нед:</span>
                <input type="number" value={rowData['__compOrders'] ?? ''} placeholder="шт"
                  onChange={e=>onChange({ ...plan, cells: { ...rowData, __compOrders: e.target.value===''?null:Number(e.target.value) } })}
                  style={{ width:80, background:'#0f111a', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, padding:'7px 10px', outline:'none', textAlign:'right' }}/>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:11, fontWeight:700, color:'rgba(200,215,235,0.6)' }}>% выкупа:</span>
                <input type="number" value={rowData['__compBuyout'] ?? ''} placeholder="%"
                  onChange={e=>onChange({ ...plan, cells: { ...rowData, __compBuyout: e.target.value===''?null:Number(e.target.value) } })}
                  style={{ width:70, background:'#0f111a', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, padding:'7px 10px', outline:'none', textAlign:'right' }}/>
              </div>
              <span style={{ fontSize:16, color:'rgba(200,215,235,0.3)' }}>=</span>
              <M label="Выкупы/нед" value={buyoutsWeek.toLocaleString('ru-RU') + ' шт'}/>
              <div style={{ width:1, height:36, background:'rgba(255,255,255,0.1)' }}/>
              <M label="Прибыль/нед (с РК)" value={fmtR(profitWeek)}/>
              <div style={{ width:1, height:36, background:'rgba(255,255,255,0.1)' }}/>
              <M label={`Инвестиция (${startPct}%)`} value={fmtR(invest)} accent="#f59e0b"/>
              <div style={{ width:1, height:36, background:'rgba(255,255,255,0.1)' }}/>
              <M label="Окупаемость" value={weeks !== null ? weeks + ' нед' : '—'}/>
              <div style={{ width:1, height:36, background:'rgba(255,255,255,0.1)' }}/>
              <M label="Дата выхода в плюс" value={plusDate} accent="#10b981"/>
            </div>
            <div style={{ marginTop:12, paddingTop:10, borderTop:'1px solid rgba(255,255,255,0.06)', fontSize:11, color:'rgba(200,215,235,0.4)', lineHeight:1.6 }}>
              Выкупы/нед = заказы ({compOrders}) × % выкупа ({compBuyout}%) = {buyoutsWeek} шт<br/>
              Прибыль/нед = выкупы ({buyoutsWeek}) × прибыль с РК на 1 ед. ({cpRK3 !== null ? fmtR(cpRK3) : '—'}) = {fmtR(profitWeek)}<br/>
              Окупаемость = инвестиция ({fmtR(invest)}) ÷ прибыль/нед ({fmtR(profitWeek)}) = {weeks !== null ? weeks + ' нед' : '—'}
            </div>
          </>}
          </div>
        );
      })()}

      {/* Калькулятор рекламы на неделю */}
      {(() => {
        const orders = Number(rowData['__ordersWeek']) || 0;
        const buyoutPct = ev.buyout || 0;
        const drrPct = params.drr != null ? Number(params.drr) : 5;
        const priceDoSpp = ev.price || 0;
        const buyouts = Math.round(orders * buyoutPct / 100);
        const revenue = Math.round(buyouts * priceDoSpp);
        const budget = Math.round(revenue * drrPct / 100);
        const fmt = (v) => v.toLocaleString('ru-RU');
        const Metric = ({ label, value, suffix, accent }) => (
          <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
            <span style={{ fontSize:10, color:'rgba(200,215,235,0.45)', textTransform:'uppercase', letterSpacing:'0.5px' }}>{label}</span>
            <span style={{ fontSize:16, fontWeight:700, color: accent || 'rgba(200,215,235,0.9)' }}>{value}{suffix}</span>
          </div>
        );
        return (
          <div style={{ marginTop:16, background:'#0a0f1a', border:'1px solid rgba(255,255,255,0.12)', borderRadius:12, padding:'16px 20px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: collapsedAdCalc?0:14, cursor:'pointer' }} onClick={()=>{const v=!collapsedAdCalc;setCollapsedAdCalc(v);if(onChange&&plan)onChange({...plan,cells:{...(plan.cells||{}),__adCalcCollapsed:v}});}}>
              <span style={{ fontSize:13, fontWeight:700, color:'#a855f7' }}>Калькулятор рекламы на неделю</span>
              <span style={{ fontSize:12, color:'rgba(200,215,235,0.4)' }}>{collapsedAdCalc?'▶':'▼'}</span>
            </div>
            {!collapsedAdCalc && <div>
            <div style={{ display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:12, fontWeight:700, color:'rgba(200,215,235,0.7)' }}>Заказы в неделю:</span>
                <input type="number" value={rowData['__ordersWeek'] ?? ''} placeholder="шт"
                  onChange={e=>onChange({ ...plan, cells: { ...rowData, __ordersWeek: e.target.value===''?null:Number(e.target.value) } })}
                  style={{ width:100, background:'#0f111a', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, padding:'7px 12px', outline:'none', textAlign:'right' }}/>
              </div>
              <div style={{ width:1, height:36, background:'rgba(255,255,255,0.1)' }}/>
              <Metric label={`Выкупы (${buyoutPct}%)`} value={fmt(buyouts)} suffix=" шт"/>
              <span style={{ fontSize:16, color:'rgba(200,215,235,0.3)' }}>×</span>
              <Metric label="Цена до СПП" value={fmt(priceDoSpp)} suffix=" ₽"/>
              <span style={{ fontSize:16, color:'rgba(200,215,235,0.3)' }}>=</span>
              <Metric label="Выручка с выкупов" value={fmt(revenue)} suffix=" ₽"/>
              <span style={{ fontSize:16, color:'rgba(200,215,235,0.3)' }}>× {drrPct}%</span>
              <div style={{ width:1, height:36, background:'rgba(255,255,255,0.1)' }}/>
              <Metric label={`Бюджет рекламы (ДРР ${drrPct}%)`} value={fmt(budget)} suffix=" ₽" accent="#10b981"/>
            </div>
            <div style={{ marginTop:12, paddingTop:10, borderTop:'1px solid rgba(255,255,255,0.06)', fontSize:11, color:'rgba(200,215,235,0.4)' }}>
              Бюджет считается от выручки с выкупов, чтобы реклама не превысила {drrPct}% от реально заработанного.
            </div>
            </div>}
          </div>
        );
      })()}

      {/* Калькулятор сверхбюджета */}
      {(() => {
        const cpoFact = unitStats?.cpo && unitStats.cpo >= 1 ? unitStats.cpo : null;
        const cpoPlan = (ev.price && params.drr) ? Math.round(ev.price * Number(params.drr) / 100 * 100) / 100 : null;
        const buyout = ev.buyout || 30;
        const pNoRK = calcNetProfit(false);
        const dailyBudget = Number(rowData['__superBudget']) || 0;
        const days = Number(rowData['__superDays']) || 0;
        if (!pNoRK || dailyBudget <= 0 || days <= 0) return (
          <div style={{ marginTop:16, background:'rgba(239,68,68,0.05)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:12, padding:'16px 20px' }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#f87171', marginBottom:8 }}>Калькулятор сверхбюджета</div>
            <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:11, color:'rgba(200,215,235,0.6)' }}>Бюджет/день:</span>
                <input type="number" value={rowData['__superBudget'] ?? ''} placeholder="₽"
                  onChange={e=>onChange({ ...plan, cells: { ...rowData, __superBudget: e.target.value===''?null:Number(e.target.value) } })}
                  style={{ width:100, background:'#0f111a', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, padding:'7px 10px', outline:'none', textAlign:'right' }}/>
                <span style={{ fontSize:11, color:'rgba(200,215,235,0.4)' }}>₽</span>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:11, color:'rgba(200,215,235,0.6)' }}>Дней:</span>
                <input type="number" value={rowData['__superDays'] ?? ''} placeholder="дн"
                  onChange={e=>onChange({ ...plan, cells: { ...rowData, __superDays: e.target.value===''?null:Number(e.target.value) } })}
                  style={{ width:80, background:'#0f111a', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, padding:'7px 10px', outline:'none', textAlign:'right' }}/>
              </div>
              {!cpoFact && !cpoPlan && <span style={{ fontSize:11, color:'rgba(239,68,68,0.7)' }}>⚠ Нет данных CPO</span>}
            </div>
          </div>
        );
        const totalInvest = Math.round(dailyBudget * days);
        const calcVariant = (cpo) => {
          if (!cpo) return null;
          const addOrders = Math.round(dailyBudget / cpo); // cpo передан как аргумент
          const addBuyouts = Math.round(addOrders * buyout / 100);
          const addProfitDay = Math.round(addBuyouts * pNoRK);
          const netDay = addProfitDay - dailyBudget;
          const paybackDays = addProfitDay > 0 ? Math.ceil(totalInvest / addProfitDay) : null;
          return { addOrders, addBuyouts, addProfitDay, netDay, paybackDays };
        };
        const vFact = calcVariant(cpoFact);
        const vPlan = calcVariant(cpoPlan);
        const fmt = v => v.toLocaleString('ru-RU');
        const fmtR = v => v.toLocaleString('ru-RU') + ' ₽';
        const M = ({label, value, accent, sub}) => (
          <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
            <span style={{ fontSize:10, color:'rgba(200,215,235,0.45)', textTransform:'uppercase' }}>{label}</span>
            <span style={{ fontSize:16, fontWeight:700, color:accent||'rgba(200,215,235,0.9)' }}>{value}</span>
            {sub && <span style={{ fontSize:10, color:'rgba(200,215,235,0.35)' }}>{sub}</span>}
          </div>
        );
        return (
          <div style={{ marginTop:16, background:'rgba(239,68,68,0.05)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:12, padding:'16px 20px' }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#f87171', marginBottom:14 }}>Калькулятор сверхбюджета</div>
            <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap', marginBottom:14 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:11, color:'rgba(200,215,235,0.6)' }}>Бюджет/день:</span>
                <input type="number" value={rowData['__superBudget'] ?? ''} placeholder="₽"
                  onChange={e=>onChange({ ...plan, cells: { ...rowData, __superBudget: e.target.value===''?null:Number(e.target.value) } })}
                  style={{ width:100, background:'#0f111a', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, padding:'7px 10px', outline:'none', textAlign:'right' }}/>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:11, color:'rgba(200,215,235,0.6)' }}>Дней:</span>
                <input type="number" value={rowData['__superDays'] ?? ''} placeholder="дн"
                  onChange={e=>onChange({ ...plan, cells: { ...rowData, __superDays: e.target.value===''?null:Number(e.target.value) } })}
                  style={{ width:80, background:'#0f111a', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, padding:'7px 10px', outline:'none', textAlign:'right' }}/>
              </div>
              <span style={{ fontSize:11, color:'rgba(200,215,235,0.4)' }}>
                {cpoFact ? `Факт CPO: ${fmtR(cpoFact)}` : '⚠ Факт CPO нет'} · Плановый CPO: {cpoPlan ? fmtR(cpoPlan) : '—'} · Выкуп: {buyout}%
              </span>
            </div>
            {/* Суммарная инвестиция — общая для обоих вариантов */}
            <div style={{ marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:11, color:'rgba(200,215,235,0.5)' }}>Суммарная инвестиция ({days} дн):</span>
              <span style={{ fontSize:16, fontWeight:700, color:'#f59e0b' }}>{fmtR(totalInvest)}</span>
            </div>
            {/* Два варианта */}
            <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
              {[{label:'Факт CPO', cpo:cpoFact, v:vFact, color:'#60a5fa'}, {label:'Плановый CPO', cpo:cpoPlan, v:vPlan, color:'#a855f7'}].map(({label, cpo, v, color})=>(
                <div key={label} style={{ flex:1, minWidth:300, padding:'12px 16px', background:'rgba(255,255,255,0.03)', borderRadius:10, border:`1px solid ${color}33` }}>
                  <div style={{ fontSize:11, fontWeight:700, color, marginBottom:10 }}>{label}: {cpo ? fmtR(cpo) : '—'}</div>
                  {!v ? <div style={{ fontSize:11, color:'rgba(200,215,235,0.3)' }}>Нет данных</div> : (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:16 }}>
                      <M label="Доп. заказов/день" value={fmt(v.addOrders)}/>
                      <M label="Доп. выкупов/день" value={fmt(v.addBuyouts)}/>
                      <M label="Доп. прибыль/день" value={fmtR(v.addProfitDay)}/>
                      <M label="Итог дня" value={fmtR(v.netDay)} accent={v.netDay>=0?'#10b981':'#ef4444'}/>
                      <M label="Окупаемость" value={v.paybackDays!=null?v.paybackDays+' дн':'—'} accent={v.paybackDays!=null&&v.paybackDays<=days?'#10b981':'#ef4444'} sub={v.paybackDays!=null&&v.paybackDays<=days?'✓ окупится':'✗ не окупится'}/>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ marginTop:10, fontSize:11, color:'rgba(200,215,235,0.35)' }}>
              CPO = ₽ на 1 заказ из факта W1 · прибыль без РК · окупаемость = суммарная инвестиция ÷ доп. прибыль/день
            </div>
          </div>
        );
      })()}

      {/* Калькулятор слива */}
      {(() => {
        const stockWb = Number(rowData['__batchQty']) || 0;
        const costTotal = ev.itogSeb || 0;
        const pNoRK = calcNetProfit(false);
        const buyout = ev.buyout || 30;
        const cpoFact = unitStats?.cpo && unitStats.cpo >= 1 ? unitStats.cpo : null;
        const weekOrders = Number(rowData['__sliveOrders']) || 0;
        const targetPct = Number(rowData['__sliveTarget']) || 70;
        const fmt = v => v != null ? v.toLocaleString('ru-RU') : '—';
        const fmtR = v => v != null ? v.toLocaleString('ru-RU') + ' ₽' : '—';

        const totalInvested = Math.round(stockWb * costTotal);
        const target = Math.round(totalInvested * targetPct / 100);
        const maxLoss = Math.round(totalInvested * (100 - targetPct) / 100);
        const weeksToEmpty = weekOrders > 0 ? Math.ceil(stockWb / weekOrders) : null;
        const totalBuyouts = weekOrders > 0 ? Math.round(stockWb * buyout / 100) : null;
        const totalProfit = totalBuyouts != null ? Math.round(totalBuyouts * (pNoRK || 0)) : null;
        const maxAdBudgetTotal = totalProfit != null ? Math.round(totalProfit + maxLoss) : null;
        const maxAdBudgetWeek = maxAdBudgetTotal != null && weeksToEmpty != null && weeksToEmpty > 0 ? Math.round(maxAdBudgetTotal / weeksToEmpty) : null;
        const maxAdBudgetDay = maxAdBudgetWeek != null ? Math.round(maxAdBudgetWeek / 7) : null;
        const weekBuyouts = Math.round(weekOrders * buyout / 100);
        const weekProfit = weekBuyouts * (pNoRK || 0);
        const weekSpend = maxAdBudgetWeek;
        const drrDay = maxAdBudgetDay && weekOrders > 0 && ev.price > 0 ? Math.round(maxAdBudgetDay / (weekOrders / 7 * ev.price * buyout / 100) * 100 * 10) / 10 : null;
        const drrWeek = maxAdBudgetWeek && weekOrders > 0 && ev.price > 0 ? Math.round(maxAdBudgetWeek / (weekOrders * ev.price * buyout / 100) * 100 * 10) / 10 : null;
        const weekNet = weekProfit - (weekSpend || 0);

        const M = ({label, value, accent, sub}) => (
          <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
            <span style={{ fontSize:10, color:'rgba(200,215,235,0.45)', textTransform:'uppercase' }}>{label}</span>
            <span style={{ fontSize:15, fontWeight:700, color:accent||'rgba(200,215,235,0.9)' }}>{value}</span>
            {sub && <span style={{ fontSize:10, color:'rgba(200,215,235,0.35)' }}>{sub}</span>}
          </div>
        );

        return (
          <div style={{ marginTop:16, background:'rgba(245,158,11,0.05)', border:'1px solid rgba(245,158,11,0.25)', borderRadius:12, padding:'16px 20px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: collapsedSliv?0:14, cursor:'pointer' }} onClick={()=>{const v=!collapsedSliv;setCollapsedSliv(v);if(onChange&&plan)onChange({...plan,cells:{...(plan.cells||{}),__slivCollapsed:v}});}}>
              <span style={{ fontSize:13, fontWeight:700, color:'#f59e0b' }}>Калькулятор слива</span>
              <span style={{ fontSize:12, color:'rgba(200,215,235,0.4)' }}>{collapsedSliv?'▶':'▼'}</span>
            </div>
            {!collapsedSliv && <>
            <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginBottom:14, alignItems:'center' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:11, color:'rgba(200,215,235,0.6)' }}>Заказов/нед (факт):</span>
                <input type="number" value={rowData['__sliveOrders'] ?? ''} placeholder="шт"
                  onChange={e=>onChange({ ...plan, cells: { ...rowData, __sliveOrders: e.target.value===''?null:Number(e.target.value) } })}
                  style={{ width:80, background:'#0f111a', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, padding:'7px 10px', outline:'none', textAlign:'right' }}/>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:11, color:'rgba(200,215,235,0.6)' }}>Цель возврата %:</span>
                <input type="number" value={rowData['__sliveTarget'] ?? 70} placeholder="%"
                  onChange={e=>onChange({ ...plan, cells: { ...rowData, __sliveTarget: e.target.value===''?null:Number(e.target.value) } })}
                  style={{ width:70, background:'#0f111a', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, padding:'7px 10px', outline:'none', textAlign:'right' }}/>
                <span style={{ fontSize:11, color:'rgba(200,215,235,0.4)' }}>%</span>
              </div>
              <span style={{ fontSize:11, color:'rgba(200,215,235,0.4)' }}>Остаток: {stockWb} шт · Себес: {fmtR(costTotal)} · Выкуп: {buyout}%</span>
            </div>
            <div style={{ display:'flex', gap:16, flexWrap:'wrap', padding:'14px 16px', background:'rgba(255,255,255,0.03)', borderRadius:10, border:'1px solid rgba(255,255,255,0.06)', marginBottom:10 }}>
              <M label="Цель (вернуть)" value={fmtR(target)} accent='#f59e0b' sub={`${stockWb} × ${fmtR(costTotal)} × ${targetPct}%`}/>
              <div style={{ width:1, height:40, background:'rgba(255,255,255,0.1)' }}/>
              <M label="Выкупов/нед" value={fmt(weekBuyouts)} sub={`${weekOrders} × ${buyout}%`}/>
              <M label="Прибыль/нед" value={fmtR(weekProfit)} sub={`${weekBuyouts} × ${fmtR(pNoRK)}`}/>
              <M label="Бюджет рекламы/нед" value={fmtR(weekSpend)} sub={cpoFact?`${weekOrders} × CPO ${fmtR(cpoFact)}`:'нет CPO'}/>
              <div style={{ width:1, height:40, background:'rgba(255,255,255,0.1)' }}/>
              <M label="ДРР день" value={drrDay!=null?drrDay+'%':'—'} accent={drrDay!=null?(drrDay<=10?'#10b981':drrDay<=20?'#f59e0b':'#ef4444'):undefined}/>
              <M label="ДРР неделя" value={drrWeek!=null?drrWeek+'%':'—'} accent={drrWeek!=null?(drrWeek<=10?'#10b981':drrWeek<=20?'#f59e0b':'#ef4444'):undefined}/>
              <div style={{ width:1, height:40, background:'rgba(255,255,255,0.1)' }}/>
              <M label="Чистый итог/нед" value={fmtR(weekNet)} accent={weekNet>=0?'#10b981':'#ef4444'}/>
              <M label="Недель до конца" value={weeksToEmpty!=null?weeksToEmpty+' нед':'—'} accent='#f59e0b' sub={weeksToEmpty!=null?`${stockWb} шт / ${weekOrders} в нед`:''}/>
            </div>

            <div style={{ fontSize:11, color:'rgba(200,215,235,0.35)', marginTop:8 }}>
              Заказы/нед вводи из факта прошлой недели · ДРР = реклама / (заказы × цена × выкуп%) · цель = остаток × себес × %
            </div>
            </>
          }
          </div>
        );
      })()}
    </div>
  );
}
